/**
 * What a loan's "Monthly Payment" actually is, on every screen that says it.
 *
 * ---------------------------------------------------------------------------
 * THE LABEL CONVENTION THIS FILE ENFORCES
 * ---------------------------------------------------------------------------
 *   "Monthly Payment"       P&I + tax escrow + HOA escrow -- what the buyer owes
 *   "Principal & Interest"  loans.monthly_payment
 *   "Tax Escrow"            loans.monthly_tax_escrow
 *   "HOA Escrow"            loans.monthly_hoa_escrow
 *
 * loans.monthly_payment is PRINCIPAL AND INTEREST ALONE -- it is the figure the
 * amortization is solved for, and it always has been. Fifteen surfaces printed
 * it under the label "Monthly Payment": the admin loan list, the mobile card,
 * the manual payment modal and its amount prefill, the buyer's portal headline,
 * their printed statement, the contract, and both cron emails. The buyer's
 * screen said 97.50 in one box and TOTAL DUE TODAY 115.49 in another, with no
 * line anywhere stating the 107.08 they actually owe or the 8.41 of card fees
 * that separates the two.
 *
 * ---------------------------------------------------------------------------
 * THIS FILE NEVER DERIVES ESCROW -- IT READS WHAT THE LOAN CARRIES
 * ---------------------------------------------------------------------------
 * The escrow charge is FROZEN on the loan at origination (migration 051,
 * loans.monthly_tax_escrow / loans.monthly_hoa_escrow). It is deliberately NOT
 * computed from properties.annual_tax_amount here or anywhere else in the
 * client: that live division is exactly what the freeze removed, and a copy of
 * it in the browser would let an admin's property edit silently re-price what
 * an existing buyer is shown.
 *
 * So every function here takes the LOAN OBJECT THE API RETURNS. If a caller has
 * only a property in hand -- the loan-creation form, where no loan exists yet --
 * it must ask the server for the figures (GET /api/admin/properties/:id/
 * escrow-preview, which runs the same lib/escrow.js helper origination will) and
 * pass them in as a loan-shaped object. That is the one legitimate use of a
 * property figure, and it still does not divide anything here.
 *
 * ---------------------------------------------------------------------------
 * WHY THE SERVER'S TOTAL WINS
 * ---------------------------------------------------------------------------
 * Every endpoint that serves loan rows now publishes a server-computed
 * total_monthly_payment. When it is present it is used verbatim. The local sum
 * is a fallback for rows from an endpoint that does not publish it yet, not a
 * second opinion -- the server's figure and this one must never be in a
 * position to disagree, so there is only one place that decides, and it is the
 * server.
 *
 * Summed in integer CENTS when it is computed here, because three doubles added
 * in dollars can land a cent away from the same three added in cents, and this
 * total is printed directly beneath its own components on several screens.
 */

/** Integer cents from a money value that may be a string, null, or absent. */
const cents = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.round(n * 100)) : 0;
};

/**
 * Principal and interest: loans.monthly_payment, under its true name.
 * @param {object} loan a loan row from the API
 * @returns {number} dollars
 */
export const principalAndInterest = (loan) => cents(loan?.monthly_payment) / 100;

/**
 * The tax escrow frozen on this loan.
 * @param {object} loan a loan row from the API
 * @returns {number} dollars
 */
export const taxEscrow = (loan) => cents(loan?.monthly_tax_escrow) / 100;

/**
 * The HOA escrow frozen on this loan. Zero on most properties.
 * @param {object} loan a loan row from the API
 * @returns {number} dollars
 */
export const hoaEscrow = (loan) => cents(loan?.monthly_hoa_escrow) / 100;

/**
 * THE MONTHLY PAYMENT: what the buyer owes each month, in full.
 *
 * Prefers the server's own total_monthly_payment. Falls back to summing the
 * three frozen columns in cents for a row served by an endpoint that does not
 * publish it.
 *
 * @param {object} loan a loan row from the API
 * @returns {number} dollars
 */
export const totalMonthlyPayment = (loan) => {
  const served = Number(loan?.total_monthly_payment);
  if (Number.isFinite(served)) return served;
  return (cents(loan?.monthly_payment)
    + cents(loan?.monthly_tax_escrow)
    + cents(loan?.monthly_hoa_escrow)) / 100;
};

/**
 * HOW FAR BEHIND SCHEDULE THIS BUYER IS.
 *
 * A memo, not a second balance: balance_remaining already carries the dollars,
 * because a short payment retires less principal. This is the figure that names
 * them, and it is added to the amount due rather than to the balance.
 *
 * @param {object} loan a loan row from the API
 * @returns {number} dollars
 */
export const arrearsBalance = (loan) => cents(loan?.arrears_balance) / 100;

/**
 * THE AMOUNT DUE: the monthly payment plus anything still carried.
 *
 * The third figure in the label convention this file enforces:
 *
 *   "Monthly Payment"  P&I + tax escrow + HOA escrow   what a period costs
 *   "Amount Due"       Monthly Payment + arrears       what squares the schedule
 *   "Cure Amount"      Amount Due + fees owed today    what clears a default
 *
 * Prefers the server's own amount_due, for the same reason totalMonthlyPayment()
 * prefers total_monthly_payment: there is one place that decides, and it is the
 * server. The local sum is a fallback for a row from an endpoint that does not
 * publish it, never a second opinion.
 *
 * Note that loan.cure_amount is the FEE-BEARING figure and is not this one. The
 * two are equal on a loan that owes no late or notice fee, which is most of
 * them, and they must not be used interchangeably on one that does.
 *
 * @param {object} loan a loan row from the API
 * @returns {number} dollars
 */
export const amountDue = (loan) => {
  const served = Number(loan?.amount_due);
  if (Number.isFinite(served)) return served;
  return (cents(totalMonthlyPayment(loan)) + cents(loan?.arrears_balance)) / 100;
};

/**
 * Every component of the monthly payment at once, for a surface that shows the
 * total with its split.
 *
 * `hasEscrow` is what a caller branches on to decide whether to render a split
 * at all: on a loan with no tax bill and no HOA the total IS the P&I figure, and
 * a "Principal & Interest $97.50" line beneath a "$97.50" total tells the reader
 * nothing while taking a row of vertical space on a phone.
 *
 * @param {object} loan a loan row from the API
 * @returns {{principalAndInterest:number, taxEscrow:number, hoaEscrow:number,
 *            total:number, hasEscrow:boolean}}
 */
export const monthlyPaymentParts = (loan) => {
  const pi = principalAndInterest(loan);
  const tax = taxEscrow(loan);
  const hoa = hoaEscrow(loan);
  return {
    principalAndInterest: pi,
    taxEscrow: tax,
    hoaEscrow: hoa,
    total: totalMonthlyPayment(loan),
    hasEscrow: tax > 0 || hoa > 0
  };
};

const money = (n) => Number(n || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

/**
 * The split as one short line, for a table cell, a card, or a tooltip where a
 * stacked list will not fit.
 *
 *   "P&I $97.50 + tax escrow $9.58"
 *   "P&I $425.00 + tax escrow $12.50 + HOA escrow $45.00"
 *
 * Returns an empty string when the loan carries no escrow, so a caller can
 * render it unconditionally and get nothing rather than a misleading
 * "P&I $97.50" beneath a $97.50 total.
 *
 * @param {object} loan a loan row from the API
 * @returns {string}
 */
export const monthlyPaymentSplitText = (loan) => {
  const parts = monthlyPaymentParts(loan);
  if (!parts.hasEscrow) return '';

  let text = `P&I $${money(parts.principalAndInterest)}`;
  if (parts.taxEscrow > 0) text += ` + tax escrow $${money(parts.taxEscrow)}`;
  if (parts.hoaEscrow > 0) text += ` + HOA escrow $${money(parts.hoaEscrow)}`;
  return text;
};

export default totalMonthlyPayment;
